import React, { useState, useEffect, useRef } from 'react';
import './FloatingChat.css';

const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const getWsUrl = () => {
  const backendUrl = getBackendUrl();
  const wsProtocol = backendUrl.startsWith('https') ? 'wss:' : 'ws:';
  const host = backendUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}//${host}/ws/chat`;
};

export default function FloatingChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [userName, setUserName] = useState(() => localStorage.getItem('ahobilam_chat_username') || '');
  const [nameSaved, setNameSaved] = useState(() => !!localStorage.getItem('ahobilam_chat_username'));
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('ahobilam_chat_session_id');
    if (!id) {
      id = 'session_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
      localStorage.setItem('ahobilam_chat_session_id', id);
    }
    return id;
  });

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Poll status & fetch history on mount
  useEffect(() => {
    checkStatus();
    fetchHistory();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  // Connect WebSockets when chat window opens
  useEffect(() => {
    if (isOpen) {
      connectWebSocket();
    } else if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, [isOpen, userName]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const checkStatus = async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/chat/status`);
      if (res.ok) {
        const data = await res.json();
        setAdminOnline(!!data.admin_online);
      }
    } catch (err) {
      console.warn("Error checking chat status:", err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/chat/messages/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
        }
      }
    } catch (err) {
      console.warn("Error fetching chat history:", err);
    }
  };

  const connectWebSocket = () => {
    try {
      const wsUrl = `${getWsUrl()}?session_id=${sessionId}&role=visitor&name=${encodeURIComponent(userName || 'Devotee')}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("WebSocket chat connected");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'status') {
            setAdminOnline(!!data.admin_online);
          } else if (data.type === 'message' || data.text) {
            setMessages((prev) => {
              // Avoid duplicate messages by ID or text+timestamp
              if (prev.some(m => m.id === data.id || (m.timestamp === data.timestamp && m.text === data.text))) {
                return prev;
              }
              return [...prev, data];
            });
          }
        } catch (e) {
          console.error("Failed parsing WS message:", e);
        }
      };

      ws.onerror = (error) => {
        console.warn("WebSocket error:", error);
      };

      ws.onclose = () => {
        console.log("WebSocket connection closed");
      };

      wsRef.current = ws;
    } catch (err) {
      console.warn("Error opening WebSocket:", err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    const textToSend = inputText.trim();
    setInputText('');

    const displayName = userName.trim() || 'Devotee';

    // Optimistic UI update
    const tempMsg = {
      session_id: sessionId,
      sender: 'visitor',
      sender_name: displayName,
      text: textToSend,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);

    // Send via WebSocket if open
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        session_id: sessionId,
        text: textToSend,
        sender_name: displayName
      }));
    } else {
      // Fallback to HTTP REST
      try {
        await fetch(`${getBackendUrl()}/api/chat/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            sender: 'visitor',
            sender_name: displayName,
            text: textToSend
          })
        });
      } catch (err) {
        console.error("Failed sending message via HTTP:", err);
      }
    }
  };

  const handleSaveName = (e) => {
    e.preventDefault();
    if (userName.trim()) {
      localStorage.setItem('ahobilam_chat_username', userName.trim());
      setNameSaved(true);
    }
  };

  return (
    <div className="floating-chat-container">
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          className={`floating-chat-btn ${adminOnline ? 'online' : 'offline'}`}
          onClick={() => setIsOpen(true)}
          title={adminOnline ? "Chat Live with Vadhyar" : "Leave a Message"}
        >
          <span className="chat-icon">💬</span>
          <span className="chat-btn-text">
            {adminOnline ? "Live Chat" : "Ask Question"}
          </span>
          <span className={`status-dot ${adminOnline ? 'dot-online' : 'dot-offline'}`} />
        </button>
      )}

      {/* Floating Chat Box Window */}
      {isOpen && (
        <div className="chat-box-window">
          {/* Header */}
          <div className="chat-box-header">
            <div className="header-info">
              <span className="header-title">🛕 Ahobilam Live Chat</span>
              <div className="header-status">
                <span className={`status-dot ${(adminOnline || messages.length > 0) ? 'dot-online' : 'dot-offline'}`} />
                <span className="status-text">
                  {(adminOnline || messages.length > 0) ? "Vadhyar is Online" : "Vadhyar is Away (Offline)"}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {messages.length > 0 && (
                <button
                  className="chat-clear-btn"
                  onClick={() => {
                    setMessages([]);
                    const newSessionId = 'session_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
                    localStorage.setItem('ahobilam_chat_session_id', newSessionId);
                    window.location.reload();
                  }}
                  title="Clear Chat History"
                >
                  🧹 Clear
                </button>
              )}
              <button className="chat-close-btn" onClick={() => setIsOpen(false)}>✕</button>
            </div>
          </div>

          {/* Name Setup Banner if not saved */}
          {!nameSaved ? (
            <form onSubmit={handleSaveName} className="chat-name-form">
              <label>Please enter your name to start chat:</label>
              <div className="name-input-group">
                <input
                  type="text"
                  placeholder="Your Name (e.g. Ramesh)"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                />
                <button type="submit" className="save-name-btn">Start</button>
              </div>
            </form>
          ) : (
            <>
              {/* Message List */}
              <div className="chat-messages-container">
                {messages.length === 0 ? (
                  <div className="chat-welcome-banner">
                    <p className="welcome-heading">🙏 Namaskaram!</p>
                    <p className="welcome-sub">
                      {adminOnline
                        ? "Vadhyar is online to assist you with Sastra, Panchangam, or Ritual questions."
                        : "Vadhyar is currently offline. Feel free to send your query here or visit our Q&A board."}
                    </p>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`chat-bubble-row ${msg.sender === 'visitor' ? 'row-visitor' : 'row-admin'}`}
                    >
                      <div className={`chat-bubble ${msg.sender === 'visitor' ? 'bubble-visitor' : 'bubble-admin'}`}>
                        <div className="bubble-sender-name">
                          {msg.sender === 'visitor' ? (msg.sender_name || 'You') : '🛕 Vadhyar'}
                        </div>
                        <div className="bubble-text">{msg.text}</div>
                        <div className="bubble-time">
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Form */}
              <form onSubmit={handleSendMessage} className="chat-input-form">
                <input
                  type="text"
                  placeholder={(adminOnline || messages.length > 0) ? "Type your message..." : "Leave your question..."}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
                <button type="submit" className="chat-send-btn">
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}
